import { DomainVisitors } from '@/modules/Domains';
import { RedirectEndpointEnum, RedirectEndpointType, RedirectMatchMode } from '@/modules/Sites';

import { ExternalRedirectTargetType } from '../externalRedirects.interfaces';

export type ExternalRedirectNodeRef = { id: string; path: string; siteId: string };
export type ExternalRedirectDomainRef = { id: string; name: string };
export type ExternalRedirectSiteRef = { id: string; name: string };

export type ExternalRedirect = {
  id: string;
  fromDomainId: string;
  fromDomain: ExternalRedirectDomainRef;
  sourceType: RedirectEndpointType;
  sourceNodeId: string | null;
  sourceNode: ExternalRedirectNodeRef | null;
  sourcePath: string | null;
  toType: ExternalRedirectTargetType;
  toDomainId: string | null;
  toDomain: ExternalRedirectDomainRef | null;
  toExternalDomain: string | null;
  toSiteId: string | null;
  toSite: ExternalRedirectSiteRef | null;
  targetType: RedirectEndpointType;
  targetNodeId: string | null;
  targetNode: ExternalRedirectNodeRef | null;
  targetPath: string | null;
  statusCode: number;
  matchMode: RedirectMatchMode;
  forVisitor: DomainVisitors;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

const endpointLabel = (
  type: RedirectEndpointType,
  node: ExternalRedirectNodeRef | null,
  path: string | null,
): string => (type === RedirectEndpointEnum.Node ? (node?.path ?? '') : (path ?? ''));

export const getExternalRedirectSourceLabel = (
  redirect: Pick<ExternalRedirect, 'fromDomain' | 'sourceType' | 'sourceNode' | 'sourcePath'>,
): string => `${ redirect.fromDomain?.name ?? '' }${ endpointLabel(redirect.sourceType, redirect.sourceNode, redirect.sourcePath) }`;

export const getExternalRedirectTargetLabel = (
  redirect: Pick<
    ExternalRedirect,
    'toType' | 'toDomain' | 'toExternalDomain' | 'toSite' | 'targetType' | 'targetNode' | 'targetPath'
  >,
): string => {
  const endpoint = endpointLabel(redirect.targetType, redirect.targetNode, redirect.targetPath);

  if (redirect.toType === ExternalRedirectTargetType.SystemDomain) return `${ redirect.toDomain?.name ?? '' }${ endpoint }`;

  if (redirect.toType === ExternalRedirectTargetType.ExternalDomain) return `${ redirect.toExternalDomain ?? '' }${ endpoint }`;

  return `Сайт "${ redirect.toSite?.name ?? '' }"${ endpoint ? ` ${ endpoint }` : '' }`;
};

export const getExternalRedirectSourceDomainLabel = (
  redirect: Pick<ExternalRedirect, 'fromDomain'>,
): string => redirect.fromDomain?.name ?? '';

export const getExternalRedirectSourceEndpointLabel = (
  redirect: Pick<ExternalRedirect, 'sourceType' | 'sourceNode' | 'sourcePath'>,
): string => endpointLabel(redirect.sourceType, redirect.sourceNode, redirect.sourcePath);

export const getExternalRedirectTargetEndpointLabel = (
  redirect: Pick<ExternalRedirect, 'targetType' | 'targetNode' | 'targetPath'>,
): string => endpointLabel(redirect.targetType, redirect.targetNode, redirect.targetPath);

export const getExternalRedirectTargetDomainLabel = (
  redirect: Pick<ExternalRedirect, 'toType' | 'toDomain' | 'toExternalDomain' | 'toSite'>,
): string => {
  if (redirect.toType === ExternalRedirectTargetType.SystemDomain) return redirect.toDomain?.name ?? '';

  if (redirect.toType === ExternalRedirectTargetType.ExternalDomain) return redirect.toExternalDomain ?? '';

  return redirect.toSite?.name ?? '';
};

export const getExternalRedirectTargetDomainTagColor = (toType: ExternalRedirectTargetType): string => {
  if (toType === ExternalRedirectTargetType.SystemDomain) return 'purple';

  if (toType === ExternalRedirectTargetType.ExternalDomain) return 'orange';

  return 'cyan';
};
